/**
 * Business Management Page (Admin)
 * 통합 업체 관리 페이지
 * - 탭 1: 업체 관리 (사업자 목록 + 승인/관리)
 * - 탭 2: 구독 관리 (구독 현황 + 만료/미결제 알림)
 * - 탭 3: 플랜 설정 (서비스 플랜 CRUD)
 */

import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { OrganizationListPage } from '../organizations/OrganizationListPage';
import { SubscriptionManagementPage } from '../../../../pages/admin/SubscriptionManagementPage';
import ServicePlansPage from './ServicePlansPage';

type TabType = 'organizations' | 'subscriptions' | 'plans';

const TAB_CONFIG: { id: TabType; label: string; icon: string }[] = [
  { id: 'organizations', label: '업체 관리', icon: '🏢' },
  { id: 'subscriptions', label: '구독 관리', icon: '💳' },
  { id: 'plans', label: '플랜 설정', icon: '📋' },
];

export const BusinessManagementPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as TabType) || 'organizations';
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    // 탭 변경 시 URL 파라미터 업데이트 (뒤로가기 지원)
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    // organizations 탭 전환 시 기존 status/page 파라미터 유지
    if (tab !== 'organizations') {
      newParams.delete('status');
      newParams.delete('page');
    }
    setSearchParams(newParams);
  };

  return (
    <div className="space-y-4">
      {/* 탭 네비게이션 */}
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px space-x-1">
          {TAB_CONFIG.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`py-3 px-5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-1.5">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* 탭 컨텐츠 */}
      <div>
        {activeTab === 'organizations' && <OrganizationListPage />}
        {activeTab === 'subscriptions' && <SubscriptionManagementPage />}
        {activeTab === 'plans' && <ServicePlansPageWrapper />}
      </div>
    </div>
  );
};

/**
 * ServicePlansPage 래퍼
 * 기존 페이지의 min-h-screen과 p-8을 제거하여 탭 안에서 잘 보이도록 함
 */
const ServicePlansPageWrapper: React.FC = () => {
  return (
    <div className="service-plans-wrapper">
      <style>{`
        .service-plans-wrapper > div {
          min-height: auto !important;
          padding: 0 !important;
        }
        .service-plans-wrapper > div > div {
          max-width: none !important;
        }
      `}</style>
      <ServicePlansPage />
    </div>
  );
};

export default BusinessManagementPage;
